/**
 * Entity editor for the site-configuration store.
 *
 * SiteConfig gets a hand-written form, which also hosts the editors for its
 * embedded header-nav tree and footer (neither is a separate entity kind —
 * see siteconfig/types.ts's fidelity note 1b). PageContent's ~90 fields
 * across 12 types are rendered generically, driven by
 * PAGECONTENT_FIELD_GROUPS rather than 12 bespoke layouts — see FIELD_META
 * below for how each field maps to an input.
 */

import { useState, type ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Switch } from "@/components/ui/switch"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { ChevronDown, ChevronRight, X, Check, History, Trash2, Plus, GripVertical, AlertTriangle } from "lucide-react"
import { useRecordDraft } from "../shared/useRecordDraft"
import { RecordStatusBar, type RecordStatus } from "../shared/components/RecordStatusBar"
import { SITECONFIG_BUCKET } from "./types"
import * as db from "./store"
import {
  type SiteConfigStore, type SiteConfigEntityKind, type PageContentType, type MenuItem,
  PAGECONTENT_FIELD_GROUPS, PAGECONTENT_TYPE_LABELS, siteConfigDisplayName,
  SERVICES_COMPONENT_FLAGS, SERVICES_DETAIL_FLAGS, SERVICES_FLAG_LABELS,
  MENU_ITEM_TYPES, MENU_ITEM_TYPE_LABELS,
} from "./types"
import type { EntityVersion } from "./history"
import type { SiteConfigView } from "./useSiteConfigCMS"
import { HistoryPanel, type HistoryVersionView } from "../shared/components/HistoryPanel"
import { Section, Field, ToggleField, ColorField } from "../shared/components/EditorFields"
import { ImageField } from "../shared/components/ImageField"
import { describeLanguage, translationStatus, type LanguageInfo, type TranslationStatus } from "../shared/languages"
import { isSiteLocale, unsupportedLocaleMessage } from "../shared/siteLanguages"
import { TranslationStatusBar } from "../shared/components/languages/TranslationStatusBar"
import { CopyableId } from "../shared/components/CopyableId"
import { HeaderMenuEditor } from "./editor/HeaderMenuEditor"
import { FooterEditor } from "./editor/FooterEditor"
import { PageContentFields } from "./editor/PageContentFields"
import { SiteFields } from "./editor/SiteFields"

interface SiteConfigEditorProps {
  store: SiteConfigStore
  // The working language, shared with the tree and the other sections
  locale: string
  languages: LanguageInfo[]
  regions?: Array<{ id: string; name: string }>
  cities?: Array<{ id: string; name: string }>
  articleCategories?: Array<{ id: string; name: string }>
  countries: Array<{ id: string; name: string }>
  kind: SiteConfigEntityKind
  id: string
  /** Which part of a siteConfig to show. Ignored for other kinds. */
  view?: SiteConfigView
  onUpdate: (kind: SiteConfigEntityKind, id: string, fields: Record<string, any>) => void
  onSetTranslation: (kind: SiteConfigEntityKind, id: string, locale: string, patch: Record<string, any>) => void
  onClose: () => void
  // Saves the editor's draft of the record as one change; false when refused
  onCommit: (kind: SiteConfigEntityKind, id: string, entity: any) => boolean
  // Where the record stands and what to do next; shown in the strip under the header
  status?: RecordStatus
  // Header chip: whether the record is indexed for meaning search
  indexBadge?: ReactNode
  // Why the store refused the last write, or null
  writeError?: string | null
  onDismissWriteError?: () => void
  versionsOf?: (entityId: string) => EntityVersion[]
  restoreEntityVersion?: (entityId: string, entryId: string, kind: SiteConfigEntityKind) =>
    { ok: true; description: string } | { ok: false; error: string }
}

/**
 * How to render each PageContent field: which HTML input, and whether it is
 * translatable (per TranslatedPageContent — see siteconfig/types.ts's
 * fidelity note 3). Fields not listed here (colors, booleans, numbers,
 * coordinates, ids) are never translatable in Directus.
 */

const SITE_DRAFT_OPS = {
  bucket: SITECONFIG_BUCKET as Record<SiteConfigEntityKind, string>,
  getEntity: db.getEntity, updateEntity: db.updateEntity, setTranslation: db.setTranslation,
}

export function SiteConfigEditor(props: SiteConfigEditorProps) {
  const { locale, languages, countries, regions = [], cities = [], articleCategories = [], kind, id, onClose, onDismissWriteError, versionsOf, restoreEntityVersion } = props
  // Fields edit a draft; Save writes it as one version
  const draft = useRecordDraft({
    section: "siteconfig", store: props.store, kind, id, ops: SITE_DRAFT_OPS,
    onCommit: props.onCommit, onUpdate: props.onUpdate, onSetTranslation: props.onSetTranslation,
  })
  const { store, onUpdate, onSetTranslation } = draft
  // As last saved, for the header
  const savedEntity = db.getEntity(props.store, kind, id)
  // A field the store refuses is reported as you type, not on Save
  const writeError = draft.error ?? props.writeError
  const view: SiteConfigView = props.view ?? "settings"
  const entity = db.getEntity(store, kind, id)
  // Translated fields edit the working language
  const editLocale = locale
  const [historyOpen, setHistoryOpen] = useState(false)
  const [restoringId, setRestoringId] = useState<string | null>(null)

  if (!entity) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
        This record no longer exists.
      </div>
    )
  }

  const title = siteConfigDisplayName(kind, entity, locale, store.defaultLocale)
  const set = (patch: Record<string, any>) => onUpdate(kind, id, patch)
  const setT = (patch: Record<string, any>) => onSetTranslation(kind, id, editLocale, patch)

  // A site's header, footer and banner fall back to the site's own default; page blocks to the store's
  const sourceLocale = kind === "site" ? entity.defaultLocale || store.defaultLocale : store.defaultLocale
  const language = languages.find(l => l.code === locale) ?? describeLanguage(locale)
  const source = languages.find(l => l.code === sourceLocale) ?? describeLanguage(sourceLocale)
  // Page blocks track review like the other sections; a site's inline text maps don't
  const statusOf = (code: string): TranslationStatus => kind === "site"
    ? (code === sourceLocale ? "source" : db.siteHasLocale(entity, code) ? "translated" : "missing")
    : translationStatus(entity.translations, code, sourceLocale)
  const status = statusOf(locale)
  // Same fallback as the Languages section: a site without its own default offers the store's
  const sitePublishes = kind !== "site" || (entity.locales ?? []).includes(locale) || sourceLocale === locale

  const rawVersions = versionsOf ? versionsOf(id) : []
  const currentEntryId = rawVersions.find(v => !v.isPriorState)?.entryId
  const versionViews: HistoryVersionView[] = rawVersions.map(v => ({
    id: v.entryId, at: v.at, action: v.action, author: v.author, actor: v.actor,
    summary: v.summary, isCurrent: v.entryId === currentEntryId, restoreUnavailable: !v.entity, snapshot: v.entity,
  }))
  const handleRestoreVersion = (entryId: string) => {
    if (!restoreEntityVersion) return
    setRestoringId(entryId)
    try {
      // The restored version replaces any unsaved edits
      draft.discard()
      const result = restoreEntityVersion(id, entryId, kind)
      if (result.ok) setHistoryOpen(false)
    } finally {
      setRestoringId(null)
    }
  }

  const canTranslate = kind === "pageContent" || kind === "site"

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start gap-2 border-b p-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">
              {kind === "site" && view !== "settings"
                ? (view === "headerNav" ? "header nav" : "footer")
                : kind}
            </Badge>
            {props.indexBadge}
          </div>
          <h2 className="mt-1 truncate text-base font-semibold">{title}</h2>
          {/*
            The id is shown for sites specifically: a frontend fetches a site
            by it, so it gets copied out regularly. Page-content blocks are
            only ever referenced from inside this CMS, so their ids stay out
            of the way.
          */}
          {kind === "site" && <CopyableId label="Site ID" id={entity.id} className="mt-0.5" />}
          {savedEntity?.updatedAt && (
            <p className="text-[11px] text-muted-foreground">Updated {new Date(savedEntity.updatedAt).toLocaleString()}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {versionsOf && (
            <Button
              variant="ghost" size="icon" className="h-8 w-8 relative"
              onClick={() => setHistoryOpen(true)}
              title={versionViews.length ? `${versionViews.length} recorded version(s)` : "No recorded changes yet"}
            >
              <History className="h-4 w-4" />
              {versionViews.length > 0 && (
                <Badge variant="secondary" className="absolute -right-1 -top-1 h-4 min-w-4 px-1 text-[9px]">
                  {versionViews.length}
                </Badge>
              )}
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <RecordStatusBar
        status={props.status}
        unsaved={draft.dirty ? { onSave: draft.save, onDiscard: draft.discard, changedElsewhere: draft.changedElsewhere } : null}
      />

      {versionsOf && restoreEntityVersion && (
        <HistoryPanel
          open={historyOpen} onOpenChange={setHistoryOpen} title={`${kind}: ${title}`}
          versions={versionViews} onRestore={handleRestoreVersion} restoringId={restoringId}
        />
      )}

      {canTranslate && (
        <TranslationStatusBar
          language={language}
          source={source}
          status={status}
          onToggleReviewed={kind === "pageContent" ? () => setT({ reviewed: status !== "reviewed" }) : undefined}
          unsupported={isSiteLocale(locale) ? undefined : unsupportedLocaleMessage(locale)}
          notListed={sitePublishes ? undefined : {
            message: `${entity.name || "This site"} doesn't offer ${language.name}, so its readers won't see this text.`,
            action: `Add ${language.name} to site`,
            onAdd: () => set({ locales: [...(entity.locales ?? []), locale] }),
          }}
        />
      )}

      {writeError && (
        <div className="flex items-start gap-2 border-b bg-amber-50 px-3 py-2 dark:bg-amber-950/30">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-400" />
          <p className="min-w-0 flex-1 text-[11px] text-amber-900 dark:text-amber-200">{writeError}</p>
          {onDismissWriteError && (
            <Button
              variant="ghost" size="icon" className="h-5 w-5 shrink-0"
              title="Dismiss" onClick={onDismissWriteError}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}

      <ScrollArea className="flex-1 min-h-0 [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
        <div className="space-y-4 p-3">
          {kind === "site" && view === "settings" && (
            <SiteFields
              entity={entity}
              countries={countries}
              regions={regions}
              cities={cities}
              articleCategories={articleCategories}
              set={set}
              editLocale={editLocale}
              defaultLocale={store.defaultLocale}
              languages={languages}
            />
          )}
          {kind === "site" && view === "headerNav" && (
            <HeaderMenuEditor
              items={entity.headerMenu ?? []}
              onChange={items => set({ headerMenu: items })}
              locale={editLocale}
            />
          )}
          {kind === "site" && view === "footer" && (
            <FooterEditor
              footer={entity.footer ?? { footerLinks: [] }}
              onChange={footer => set({ footer })}
              locale={editLocale}
            />
          )}
          {kind === "pageContent" && (
            <PageContentFields entity={entity} editLocale={editLocale} defaultLocale={store.defaultLocale} set={set} setT={setT} />
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

// ── SiteConfig ───────────────────────────────────────────────────────────────

